import React, { useState, useEffect } from "react";
import { Card } from "../../components/Card";
import { Loading } from "../../components/Loader";
import AppClass from "../../AppClass";
import toastMessage from "../../components/toastMessage";
import moment from "moment";
import "./LoanApplicationDetails.css";
import { formatMoney } from "../../configs/formatCurrency";
import StatusBadge from "../../components/StatusBadge";

const LoanDetails = ({ somethingID, user_id }) => {
  const [loanData, setLoanData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [decisionRemarks, setDecisionRemarks] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isFeesExpanded, setIsFeesExpanded] = useState(false);

  useEffect(() => {
    getLoanById(somethingID);
  }, [somethingID]);

  const getLoanById = async (id) => {
    setIsLoading(true);
    try {
      const response = await AppClass.get_loan_by_id(id);
      setLoanData(response?.data?.data);
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || err?.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleApproveLoan = async () => {
    setIsSubmitting(true);
    try {
      await AppClass.approve_loan(somethingID, {
        approved_by_id: user_id,
        approval_remarks: decisionRemarks,
      });
      toastMessage("success", "Loan approved successfully");
      getLoanById(somethingID);
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || err?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRejectLoan = async () => {
    if (!decisionRemarks.trim()) {
      toastMessage("error", "Please provide remarks for rejection");
      return;
    }

    setIsSubmitting(true);
    try {
      await AppClass.reject_loan(somethingID, {
        rejected_by_id: user_id,
        rejection_remarks: decisionRemarks,
      });
      toastMessage("success", "Loan rejected successfully");
      getLoanById(somethingID);
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || err?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmitForReview = async () => {
    setIsSubmitting(true);
    try {
      await AppClass.update_data(somethingID, {
        status: "under_review",
      });
      toastMessage("success", "Loan submitted for review successfully");
      getLoanById(somethingID);
      window.location.href = `/home/admin/view_loan_application/${somethingID}`;
    } catch (err) {
      toastMessage("error", err?.response?.data?.error || err?.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) return <Loading message="Loading loan details..." />;
  if (!loanData) return <div>No loan details found</div>;

  return (
    <div className="content-cont">
      <div className="page-header">
        <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
          <div>
            <button className="back-btn">
              <a href="/home/admin/applications">
                <i className="fa fa-arrow-left mr-2"></i> Back to List
              </a>
            </button>
          </div>
          <div className="page-header-title">
            <h2 className="">Loan Application Review</h2>
            <p className="text-muted">Review and process loan application <span className="text-primary">{loanData.loan_id}</span></p>
          </div>
        </div>
        <div className="">
          <StatusBadge status={loanData.status} type="loan" />
        </div>
      </div>

      <div className="body-content">
        <div className="row">
          {/* Member Details Card */}
          <div className="col-md-4">
            <Card>
              <div className="member-details-card">
                <h3 className="section-title">
                  <i className="fa fa-user"></i> Member Details
                </h3>
                
                <div className="member-header">
                  <div className="avatar">
                    {loanData.member?.user_name?.substring(0, 2).toUpperCase()}
                  </div>
                  <div className="member-info">
                    <h4>{loanData.member?.user_name}</h4>
                    <p className="text-muted mb-0">Member Number: {loanData.member?._id}</p>
                  </div>
                </div>

                <div className="details-list">
                  <div className="detail-item">
                    <span className="label">Phone Number</span>
                    <span className="value">{loanData.member?.phone_number}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Application Date</span>
                    <span className="value">{moment(loanData.created_at).format('DD/MM/YYYY, HH:mm')}</span>
                  </div>
                  <div className="detail-item">
                    <span className="label">Created By</span>
                    <span className="value">{loanData.created_by?.user_name}</span>
                  </div>
                </div>
              </div>
            </Card>
          </div>

          {/* Loan Details Card */}
          <div className="col-md-8">
            <Card>
              <div className="loan-details-card">
                <h3 className="section-title">
                  <i className="fa fa-file-invoice"></i> Loan Details
                </h3>
                <p className="text-muted">{loanData.loan_product?.name} ({loanData.loan_product?.code})</p>

                <div className="row g-4 loan-summary">
                  <div className="col-md-4">
                    <div className="summary-box">
                      <label>Principal Amount</label>
                      <h4>KES {formatMoney(loanData.principal_amount)}</h4>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="summary-box">
                      <label>Interest Rate</label>
                      <h4>{loanData.interest_rate}% ({loanData.interest_frequency})</h4>
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="summary-box">
                      <label>Duration</label>
                      <h4>{loanData.duration} {loanData.duration_unit}</h4>
                    </div>
                  </div>
                </div>

                <div className="fees-section mt-4 mb-4">
                  <div 
                    className="fees-header" 
                    onClick={() => setIsFeesExpanded(!isFeesExpanded)}
                    style={{ cursor: 'pointer' }}
                  >
                    <h4>
                      <i className={`fa fa-chevron-${isFeesExpanded ? 'down' : 'right'} mr-2`}></i>
                      Applicable Fees ({loanData.fees.length}) <span className="text-muted">(KES {formatMoney(loanData?.total_fees)})</span>
                    </h4>
                  </div>
                  
                  {isFeesExpanded && (
                    <div className="fees-content">
                      {loanData.fees && loanData.fees.length > 0 ? (
                        <div className="fees-table">
                          <table className="table table-bordered">
                            <thead>
                              <tr>
                                <th>Fee</th>
                                <th className="text-right">Amount (KES)</th>
                              </tr>
                            </thead>
                            <tbody>
                              {loanData.fees.map((fee, index) => (
                                <tr key={index}>
                                  <td>{fee.name.replace(/_/g, ' ').toLowerCase()
                                         .split(' ')
                                         .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                                         .join(' ')}
                                  </td>
                                  <td className="text-right">{formatMoney(fee.amount)}</td>
                                </tr>
                              ))}
                              <tr className="total-row">
                                <td><strong>Total Fees</strong></td>
                                <td className="text-right">
                                  <strong>
                                    {formatMoney(loanData?.total_fees)}
                                  </strong>
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <p className="text-muted">No fees have been applied to this loan.</p>
                      )}
                    </div>
                  )}
                </div>

                <div className="row mt-4">
                  <div className="col-md-6">
                    <div className="detail-item">
                      <span className="label">Start Date</span>
                      <span className="value">{moment(loanData.start_date).format('DD/MM/YYYY, HH:mm')}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">End Date</span>
                      <span className="value">{moment(loanData.end_date).format('DD/MM/YYYY, HH:mm')}</span>
                    </div>
                  </div>
                  <div className="col-md-6">
                    <div className="detail-item">
                      <span className="label">Loan ID</span>
                      <span className="value">{loanData.loan_id}</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Amortization Method</span>
                      <span className="value">{loanData.amortization_method.replace(/_/g, ' ')}</span>
                    </div>
                  </div>
                </div>

                <div className="repayment-schedule mt-4">
                  <h4>Repayment Schedule</h4>
                  <p className="text-muted">No repayment schedule has been generated yet. This will be available after loan approval.</p>
                </div>
              </div>
            </Card>
          </div>
        </div>

        {/* Loan Decision Section - Full Width */}
        {['draft', 'under_review'].includes(loanData.status) && (
          <div className="col-12 mt-4">
            <Card>
              <div className="loan-decision-card">
                <h3 className="section-title">
                  <i className="fa fa-gavel"></i>
                  {loanData.status === 'draft' ? 'Submit Application' : 'Loan Decision'}
                </h3>
                <p className="text-muted">
                  {loanData.status === 'draft' 
                    ? 'Review the application details and submit for approval'
                    : 'Review the application details and make a decision'}
                </p>

                <div className="alert alert-warning">
                  <i className="fa fa-exclamation-triangle"></i>
                  <div className="alert-content">
                    {loanData.status === 'draft'
                      ? 'Please carefully review all loan details before submitting. Once submitted, modifications will require admin approval.'
                      : 'Please carefully review all loan details before making a decision. Once approved or rejected, this action will be recorded and cannot be easily reversed.'}
                  </div>
                </div>

                {loanData.status === 'under_review' && (
                  <div className="form-group mt-4">
                    <label>
                      {loanData.status === 'draft' ? 'Submission Remarks' : 'Decision Remarks'}
                    </label>
                  <textarea
                    className="form-control"
                    rows={4}
                    value={decisionRemarks}
                    onChange={(e) => setDecisionRemarks(e.target.value)}
                    placeholder={loanData.status === 'draft' 
                      ? "Enter any notes or comments regarding your submission..."
                      : "Enter any notes or comments regarding your decision..."}
                  />
                </div>)}

                <div className="decision-buttons">
                  {loanData.status === 'draft' ? (
                    <button 
                      className="btn btn-primary"
                      onClick={handleSubmitForReview}
                      disabled={isSubmitting}
                    >
                      <i className={`fa ${isSubmitting ? 'fa-spinner fa-spin' : 'fa-paper-plane'} mr-2`}></i>
                      {isSubmitting ? ' Submitting...' : ' Submit for Review'}
                    </button>
                  ) : (
                    <>
                      <button 
                        className="btn btn-danger"
                        onClick={handleRejectLoan}
                        disabled={isSubmitting}
                      >
                        <i className="fa fa-times mr-2"></i> Reject Loan
                      </button>
                      <button 
                        className="btn btn-success"
                        onClick={handleApproveLoan}
                        disabled={isSubmitting}
                      >
                        <i className="fa fa-check mr-2"></i> Approve Loan
                      </button>
                    </>
                  )}
                </div>
              </div>
            </Card>
          </div>
        )}
      </div>
      </div>
    );
};

export default LoanDetails;
